<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 glass-modal-backdrop animate-fadeIn"
    @click.self="emit('close')"
  >
    <div
      class="glass-modal-container w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] text-left transition-all"
    >
      <!-- HEADER DO MODAL (GLASSMORPHISM CLEAN) -->
      <div
        class="px-6 py-4 border-b border-slate-200/80 dark:border-[#26282d] bg-slate-50/70 dark:bg-[#141517]/80 backdrop-blur-md flex items-center justify-between shrink-0"
      >
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-[#1F93FF]/15 text-[#1F93FF] dark:text-[#38BDF8] border border-[#1F93FF]/30 flex items-center justify-center shadow-2xs">
            <span class="text-base">⚖️</span>
          </div>
          <div>
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100 tracking-tight">
                Auditoria Processual & Prazos IA
              </h3>
              <span class="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#EBF5FF] text-[#1F93FF] dark:bg-[#0B2545] dark:text-[#38BDF8] border border-[#1F93FF]/30">
                Motor Viabilidade Zetijuris
              </span>
            </div>
            <p class="text-xs font-normal text-slate-500 dark:text-slate-400 mt-0.5">
              Leitura de andamentos paginados, decisões judiciais e diagnóstico de providências
            </p>
          </div>
        </div>

        <button
          type="button"
          class="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          title="Fechar"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4.5"></span>
        </button>
      </div>

      <!-- CORPO DO MODAL (DESIGN SYSTEM 17 THEMES) -->
      <div class="flex-1 overflow-y-auto p-6 space-y-4 text-xs custom-scrollbar">
        <!-- 1. IDENTIFICAÇÃO DO PROCESSO AUDITADO -->
        <div class="p-3.5 rounded-xl bg-slate-50/80 dark:bg-[#1a1b1e]/80 border border-slate-200/80 dark:border-[#26282d] flex flex-wrap items-center justify-between gap-3 shadow-2xs">
          <div>
            <div class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">
              Processo Selecionado
            </div>
            <div class="flex items-center gap-2 mt-0.5">
              <span class="font-mono text-sm font-semibold text-slate-900 dark:text-slate-100 select-all">
                {{ process?.number || 'Número não informado' }}
              </span>
              <button
                v-if="process?.number"
                type="button"
                class="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors p-0.5 cursor-pointer"
                title="Copiar CNJ"
                @click="copyText(process.number, 'CNJ')"
              >
                <span class="text-xs">📋</span>
              </button>
              <a
                v-if="processUrl"
                :href="processUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="text-[#1F93FF] hover:underline inline-flex items-center gap-1 font-semibold text-xs transition-colors ml-1"
                title="Abrir no Tribunal"
              >
                <span>Tribunal</span>
                <span>🔗</span>
              </a>
            </div>
          </div>

          <div class="text-right">
            <div class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">
              Cliente / Requerente
            </div>
            <div class="font-semibold text-slate-900 dark:text-slate-100 text-xs mt-0.5">
              {{ contact?.name || 'Cliente' }}
            </div>
          </div>
        </div>

        <!-- 2. ESTADO DE CARREGAMENTO / SCANNING EM TEMPO REAL -->
        <div v-if="isLoading" class="py-14 flex flex-col items-center justify-center space-y-4 text-center">
          <div class="relative flex items-center justify-center">
            <div class="w-16 h-16 rounded-full border-4 border-[#1F93FF]/20 border-t-[#1F93FF] animate-spin"></div>
            <div class="absolute text-xl">⚖️</div>
          </div>
          <div class="space-y-1">
            <h4 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {{ loadingMessage }}
            </h4>
            <p class="text-xs text-slate-500 dark:text-slate-400 max-w-sm font-normal">
              Conectando aos servidores do Tribunal e processando o inteiro teor das decisões e andamentos...
            </p>
          </div>
        </div>

        <!-- 3. RESULTADO COMPLETO DA AUDITORIA -->
        <div v-else-if="auditData" class="space-y-4">
          <!-- BANNER DE STATUS & GRAU DE URGÊNCIA (PALETA OFICIAL DS 17) -->
          <div
            class="p-4 rounded-xl border flex items-start gap-3.5 transition-all shadow-2xs"
            :class="urgencyBannerClass"
          >
            <div class="text-2xl shrink-0 mt-0.5">
              {{ urgencyIcon }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md" :class="urgencyBadgeClass">
                  {{ auditData.diagnostico?.grau_urgencia || 'ANÁLISE CONCLUÍDA' }}
                </span>
                <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-black/10 dark:bg-white/10">
                  {{ auditData.diagnostico?.tipo_ato || 'ATO PROCESSUAL' }}
                </span>
              </div>
              <h4 class="text-sm font-semibold mt-1.5 leading-snug">
                {{ auditData.diagnostico?.acao_pratica_imediata || 'Acompanhamento Regular do Feito' }}
              </h4>
            </div>
          </div>

          <!-- QUADRO: O QUE O ADVOGADO DEVE FAZER AGORA -->
          <div class="p-4 rounded-xl bg-white dark:bg-[#1a1b1e] border border-slate-200/80 dark:border-[#26282d] space-y-3 shadow-2xs">
            <div class="flex items-center justify-between pb-2.5 border-b border-slate-100 dark:border-[#26282d]">
              <span class="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5 uppercase tracking-wide">
                <span>🎯</span> Providência Necessária (Instrução Prática)
              </span>
              <span v-if="auditData.diagnostico?.prazo_dias" class="text-xs font-medium px-2.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] dark:bg-[#78350F]/40 dark:text-[#FDE68A] border border-amber-300/60 dark:border-amber-700/60">
                Prazo: {{ auditData.diagnostico.prazo_dias }} dias úteis (Limite: {{ auditData.diagnostico.data_limite_estimada || 'Calculando' }})
              </span>
            </div>

            <!-- Texto Explicativo Inequívoco -->
            <p class="text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-normal whitespace-pre-wrap">
              {{ auditData.diagnostico?.o_que_fazer }}
            </p>

            <!-- CHECKLIST DE DOCUMENTOS OBRIGATÓRIOS -->
            <div v-if="auditData.diagnostico?.documentos_necessarios && auditData.diagnostico.documentos_necessarios.length" class="mt-3 pt-3 border-t border-slate-100 dark:border-[#26282d] space-y-2">
              <div class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">
                Documentos & Peças Exigidas para Juntada:
              </div>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(doc, dIdx) in auditData.diagnostico.documentos_necessarios"
                  :key="dIdx"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#EBF5FF] text-[#0f4c81] dark:bg-[#0B2545] dark:text-[#93c5fd] border border-[#1F93FF]/20 flex items-center gap-1.5"
                >
                  <span class="text-[#1F93FF]">📎</span>
                  <span>{{ doc }}</span>
                </span>
              </div>
            </div>
          </div>

          <!-- RESUMO DA DECISÃO OU DESPACHO DO JUIZ -->
          <div class="p-4 rounded-xl bg-slate-50/80 dark:bg-[#1a1b1e]/60 border-l-4 border-l-[#1F93FF] border border-slate-200/80 dark:border-[#26282d] space-y-1.5">
            <div class="flex items-center justify-between">
              <span class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider flex items-center gap-1.5">
                <span>⚖️</span> Síntese do Último Despacho / Decisão Judicial
              </span>
              <span class="text-xs text-slate-400 font-medium">
                Data do Ato: {{ auditData.diagnostico?.data_ultimo_andamento || 'Recente' }}
              </span>
            </div>
            <div class="text-xs text-slate-600 dark:text-slate-300 italic leading-relaxed pt-1">
              "{{ auditData.diagnostico?.sintese_decisao_despacho || 'Sem despacho com carga de providência pendente.' }}"
            </div>
          </div>

          <!-- HISTÓRICO DE ANDAMENTOS PAGINADOS (LINHA DO TEMPO) -->
          <div class="space-y-2 pt-1">
            <div class="flex items-center justify-between px-0.5">
              <span class="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider flex items-center gap-1.5">
                <span>⏱️</span> Últimos Andamentos Auditados (Tribunal)
              </span>
              <span class="text-xs text-slate-400 font-medium">
                {{ auditData.historico_andamentos?.length || 0 }} andamentos lidos
              </span>
            </div>

            <div class="space-y-2 max-h-44 overflow-y-auto custom-scrollbar pr-1">
              <div
                v-for="(andamento, aIdx) in auditData.historico_andamentos"
                :key="aIdx"
                class="p-3 rounded-xl bg-slate-50/60 dark:bg-[#1a1b1e]/40 border border-slate-100 dark:border-[#26282d] flex items-start gap-3 text-xs"
              >
                <span class="px-2 py-0.5 rounded bg-slate-200/80 dark:bg-slate-700/80 font-mono text-xs font-semibold text-slate-700 dark:text-slate-300 shrink-0">
                  {{ andamento.data }}
                </span>
                <div class="flex-1 min-w-0">
                  <div class="font-semibold text-slate-900 dark:text-slate-100">
                    {{ andamento.descricao }}
                  </div>
                  <div v-if="andamento.detalhe" class="text-slate-500 dark:text-slate-400 text-xs mt-0.5 line-clamp-2 font-normal">
                    {{ andamento.detalhe }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. ESTADO DE ERRO OU VAZIO -->
        <div v-else class="py-14 text-center text-slate-400 font-normal">
          Não foi possível carregar o diagnóstico deste processo. Clique em Reauditar.
        </div>
      </div>

      <!-- FOOTER DO MODAL (DESIGN SYSTEM 17 BOTÕES CANÔNICOS) -->
      <div
        class="px-6 py-3.5 border-t border-slate-200/80 dark:border-[#26282d] bg-slate-50/70 dark:bg-[#141517]/80 backdrop-blur-md flex items-center justify-between gap-3 flex-wrap shrink-0"
      >
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="px-4 py-2 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer flex items-center gap-1.5"
            title="Copiar parecer estruturado"
            @click="copyReport"
          >
            <span>📋</span>
            <span>Copiar Parecer</span>
          </button>

          <button
            type="button"
            class="px-4 py-2 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer flex items-center gap-1.5"
            title="Forçar nova leitura no Tribunal"
            :disabled="isLoading"
            @click="runAudit"
          >
            <span :class="{ 'animate-spin': isLoading }">🔄</span>
            <span>Reauditar</span>
          </button>
        </div>

        <div class="flex items-center gap-2">
          <!-- BOTÃO PRIMÁRIO NATIVO CHATWOOT (#1F93FF) -->
          <button
            v-if="auditData?.diagnostico?.providencia_obrigatoria"
            type="button"
            class="px-5 py-2 text-xs font-medium rounded-lg bg-[#1F93FF] hover:bg-[#1877d2] text-white transition-colors cursor-pointer shadow-xs flex items-center gap-1.5"
            :disabled="isCreatingTask"
            @click="createTaskAutomatically"
          >
            <span>⚡</span>
            <span>{{ isCreatingTask ? 'Criando Prazo...' : 'Criar Prazo Automático' }}</span>
          </button>

          <button
            type="button"
            class="px-4 py-2 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            @click="emit('close')"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TarefasAPI from 'dashboard/api/tarefas';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  process: {
    type: Object,
    default: () => ({}),
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'taskCreated']);
const store = useStore();

const isLoading = ref(false);
const loadingMessage = ref('Conectando ao Tribunal (TJSP / CNJ)...');
const isCreatingTask = ref(false);
const auditData = ref(null);

const processUrl = computed(() => {
  if (props.process?.url) return props.process.url;
  if (props.process?.number) {
    const rawNumber = props.process.number.replace(/[^0-9.-]/g, '');
    return `https://esaj.tjsp.jus.br/cpopg/search.do?cbPesquisa=NUMPROC&dadosConsulta.valorConsulta=${encodeURIComponent(rawNumber)}`;
  }
  return '';
});

const urgencyBannerClass = computed(() => {
  const urg = (auditData.value?.diagnostico?.grau_urgencia || '').toUpperCase();
  if (urg.includes('FATAL') || urg.includes('URGENTE') || urg.includes('EXTINCAO')) {
    return 'bg-[#FCDADD]/90 dark:bg-[#331919]/90 border-rose-300 dark:border-[#592424] text-rose-950 dark:text-rose-100';
  }
  if (urg.includes('ALTA') || urg.includes('PENDENTE') || urg.includes('ACAO') || urg.includes('REPLICA')) {
    return 'bg-[#EEF6FF]/90 dark:bg-[#1D232F]/90 border-[#bcdcfe] dark:border-[#2b3547] text-[#0B2545] dark:text-[#EBF5FF]';
  }
  return 'bg-emerald-50/90 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800/60 text-emerald-900 dark:text-emerald-200';
});

const urgencyBadgeClass = computed(() => {
  const urg = (auditData.value?.diagnostico?.grau_urgencia || '').toUpperCase();
  if (urg.includes('FATAL') || urg.includes('URGENTE')) {
    return 'bg-rose-600 text-white';
  }
  if (urg.includes('ALTA') || urg.includes('PENDENTE') || urg.includes('ACAO') || urg.includes('REPLICA')) {
    return 'bg-[#1F93FF] text-white';
  }
  return 'bg-emerald-600 text-white';
});

const urgencyIcon = computed(() => {
  const urg = (auditData.value?.diagnostico?.grau_urgencia || '').toUpperCase();
  if (urg.includes('FATAL')) return '⚡';
  if (urg.includes('URGENTE')) return '🚨';
  if (urg.includes('ALTA') || urg.includes('REPLICA')) return '⚠️';
  return '✅';
});

const copyText = (text, label) => {
  if (!text) return;
  navigator.clipboard?.writeText(text);
  useAlert(`${label || 'Texto'} copiado com sucesso!`);
};

const copyReport = () => {
  if (!auditData.value) return;
  const d = auditData.value.diagnostico;
  const report = `📋 AUDITORIA PROCESSUAL IA • ${auditData.value.processo_numero}\n` +
    `👤 Cliente: ${props.contact?.name || 'Cliente'}\n` +
    `🚨 Urgência: ${d?.grau_urgencia || 'REGULAR'} (${d?.prazo_dias || 0} dias úteis)\n` +
    `🎯 Ação Imediata: ${d?.acao_pratica_imediata}\n\n` +
    `📝 O que fazer:\n${d?.o_que_fazer}\n\n` +
    `📎 Documentos: ${(d?.documentos_necessarios || []).join(', ')}\n\n` +
    `⚖️ Despacho: "${d?.sintese_decisao_despacho}"`;

  copyText(report, 'Parecer Completo');
};

const runAudit = async () => {
  if (!props.process?.number) return;
  isLoading.value = true;
  auditData.value = null;

  try {
    loadingMessage.value = 'Conectando ao Tribunal (TJSP / e-SAJ)...';
    await new Promise(r => setTimeout(r, 300));

    loadingMessage.value = 'Lendo andamentos paginados, decisões e publicações DJE...';
    await new Promise(r => setTimeout(r, 400));

    loadingMessage.value = 'Engine IA analisando providências jurídicas e documentos exigidos...';

    const payload = {
      process_number: props.process.number,
      court: props.process.court || '',
      type: props.process.type || '',
      contact_name: props.contact?.name || '',
    };

    let resData = null;
    let lastError = null;

    const endpoints = [
      'https://viabilidade.zetijuris.com.br/api/audit-process',
      'https://viabilidade.zetijuris.com.br/webhook/audit-process'
    ];

    for (const url of endpoints) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          const json = await response.json();
          if (json && json.sucesso) {
            resData = json;
            break;
          } else if (json && json.error) {
            lastError = json.error;
          }
        }
      } catch (err) {
        lastError = err.message;
      }
    }

    if (!resData || !resData.diagnostico) {
      throw new Error(lastError || 'Não foi possível obter o diagnóstico em tempo real do Tribunal.');
    }

    auditData.value = resData;
  } catch (e) {
    console.error('Erro na auditoria processual:', e);
    useAlert('Erro na auditoria do processo: ' + (e.message || 'Falha de comunicação.'));
  } finally {
    isLoading.value = false;
  }
};

const createTaskAutomatically = async () => {
  if (!auditData.value?.diagnostico || !props.contact?.id) return;
  isCreatingTask.value = true;
  try {
    const d = auditData.value.diagnostico;
    
    let dueDateStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(Date.now() + (d.prazo_dias || 15) * 86400000));

    await TarefasAPI.create({
      tarefa: {
        title: d.acao_pratica_imediata,
        description: `[AÇÃO NECESSÁRIA - AUDITORIA IA]\n${d.o_que_fazer}\n\nDocumentos exigidos: ${(d.documentos_necessarios || []).join(', ')}`,
        due_date: dueDateStr,
        due_time: '18:00',
        priority: 'urgente',
        is_fatal_deadline: d.grau_urgencia === 'FATAL',
        process_number: props.process?.number || '',
        contact_id: props.contact.id,
      },
    });

    useAlert('⚡ Prazo processual criado com sucesso na agenda do cliente!');
    store.dispatch('tarefas/getSummary');
    emit('taskCreated');
  } catch (e) {
    console.error('Erro ao criar tarefa automática:', e);
    useAlert('Erro ao criar tarefa: ' + e.message);
  } finally {
    isCreatingTask.value = false;
  }
};

watch(
  () => props.show,
  (isOpen) => {
    if (isOpen && props.process?.number) {
      runAudit();
    } else {
      auditData.value = null;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fadeIn {
  animation: fadeIn 0.15s ease-out forwards;
}

.glass-modal-backdrop {
  backdrop-filter: blur(8px) !important;
  -webkit-backdrop-filter: blur(8px) !important;
  background-color: rgba(15, 23, 42, 0.65) !important;
}

.glass-modal-container {
  background-color: #FEFEFE !important;
  border: 1px solid rgba(226, 232, 240, 0.9) !important;
  border-radius: 1rem !important;
}

:global(.dark) .glass-modal-container,
:global(body.dark) .glass-modal-container,
.dark .glass-modal-container {
  background-color: #141517 !important;
  border-color: #26282d !important;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.35);
  border-radius: 4px;
}
</style>
